import React from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckSquare, ArrowUpRight, Users, TrendingUp, AlertTriangle, DollarSign,
  Plus, Upload, Eye, Activity, FileText, Trash2, Send, Play, CheckCircle, Mail
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import DashboardTasksWidget from '../DashboardTasksWidget';

// Small reusable card frame
const StatCard = ({ value, label, sublabel, color = 'text-gray-900', loading }) => (
  <div className="h-full">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">{label}</div>
    <div className={`text-3xl font-bold ${color}`}>
      {loading ? '—' : value}
    </div>
    {sublabel && <div className="text-xs text-gray-500 mt-1">{sublabel}</div>}
  </div>
);

// ---- Task widgets -------------------------------------------------------

export const MyOpenTasksWidget = () => {
  const { data: tasks = [], isLoading } = useQuery(
    ['widget', 'tasks', 'me', 'open'],
    () => api.get("/tasks", { params: { assignedTo: "me", completed: "false" }, silent: true }).then((r) => r.data),
    { staleTime: 30 * 1000 }
  );
  return (
    <Link to="/tasks" className="block h-full hover:opacity-80">
      <StatCard
        value={tasks.length}
        label="My open tasks"
        sublabel={tasks.length > 0 ? 'Click to manage' : 'Inbox zero ✨'}
        loading={isLoading}
      />
    </Link>
  );
};

export const DueTodayWidget = () => {
  const { data: tasks = [], isLoading } = useQuery(
    ['widget', 'tasks', 'me', 'open'],
    () => api.get("/tasks", { params: { assignedTo: "me", completed: "false" }, silent: true }).then((r) => r.data),
    { staleTime: 30 * 1000 }
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today.getTime() + 86400000);
  const due = tasks.filter((t) => {
    if (!t.dueDate) return false;
    const d = new Date(t.dueDate);
    return d >= today && d < tomorrow;
  });
  return (
    <Link to="/tasks" className="block h-full hover:opacity-80">
      <StatCard
        value={due.length}
        label="Due today"
        color={due.length > 0 ? 'text-blue-600' : 'text-gray-900'}
        sublabel={due.length > 0 ? 'Tasks to close before midnight' : 'Nothing on deck'}
        loading={isLoading}
      />
    </Link>
  );
};

export const OverdueTasksWidget = () => {
  const { data: tasks = [], isLoading } = useQuery(
    ['widget', 'tasks', 'me', 'open'],
    () => api.get("/tasks", { params: { assignedTo: "me", completed: "false" }, silent: true }).then((r) => r.data),
    { staleTime: 30 * 1000 }
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = tasks.filter((t) => t.dueDate && new Date(t.dueDate) < today);
  return (
    <Link to="/tasks" className="block h-full hover:opacity-80">
      <StatCard
        value={overdue.length}
        label="Overdue tasks"
        color={overdue.length > 0 ? 'text-red-600' : 'text-gray-900'}
        sublabel={overdue.length > 0 ? 'Past due — needs attention' : 'All caught up ✓'}
        loading={isLoading}
      />
    </Link>
  );
};

export const UpcomingMeetingsWidget = () => {
  const { data: tasks = [], isLoading } = useQuery(
    ['widget', 'tasks', 'me', 'meetings'],
    () => api.get("/tasks", { params: { assignedTo: "me", completed: "false", type: "meeting" }, silent: true }).then((r) => r.data),
    { staleTime: 30 * 1000 }
  );
  const upcoming = [...tasks]
    .filter((t) => t.dueDate && new Date(t.dueDate) >= new Date())
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 5);
  return (
    <div className="h-full">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Upcoming meetings</div>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : upcoming.length === 0 ? (
        <p className="text-sm text-gray-400">No upcoming meetings.</p>
      ) : (
        <ul className="space-y-1.5">
          {upcoming.map((t) => (
            <li key={t._id} className="flex items-start justify-between gap-2 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{t.title}</div>
                {t.leadId && <div className="text-xs text-primary-600 truncate">{t.leadId.firstName} {t.leadId.lastName}</div>}
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap">
                {new Date(t.dueDate).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---- Lead / pipeline widgets -------------------------------------------

export const NewLeadsThisWeekWidget = () => {
  const { data: stats, isLoading } = useQuery(
    ['widget', 'stats', 'week'],
    () => api.get("/leads/stats/overview", { params: { timeRange: "week" }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const count = stats?.overview?.totalLeads ?? 0;
  return (
    <Link to="/leads" className="block h-full hover:opacity-80">
      <StatCard
        value={count}
        label="New leads (7d)"
        sublabel="Created in the last 7 days"
        loading={isLoading}
      />
    </Link>
  );
};

export const PipelineValueWidget = () => {
  const { data: stats, isLoading } = useQuery(
    ['widget', 'stats', 'all'],
    () => api.get("/leads/stats/overview", { params: { timeRange: "all" }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const value = stats?.overview?.totalValueConverted ?? stats?.overview?.totalValue ?? 0;
  return (
    <Link to="/pipeline" className="block h-full hover:opacity-80">
      <StatCard
        value={formatCurrency(value)}
        label="Pipeline value"
        sublabel="Open leads · estimated"
        loading={isLoading}
      />
    </Link>
  );
};

// Customers won since the 1st of this month (by their won date), from the
// sales report, which counts wins the same way the Customers list does.
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); };

// The sales report needs view_reports; without it (or on any failure) the
// card says so instead of showing a false 0.
const reportErrorLabel = (error) => (error?.response?.status === 403 ? 'Needs report access' : 'Could not load');

export const WonThisMonthWidget = () => {
  const from = monthStart();
  const { data: report, isLoading, isError, error } = useQuery(
    ['widget', 'reports', 'won-month', from.toISOString()],
    () => api.get('/reports/sales', { params: { from: from.toISOString(), to: new Date().toISOString() }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const won = report?.winRate?.won ?? 0;
  const revenue = report?.winRate?.totalRevenue ?? 0;
  return (
    <Link to={`/customers?wonFrom=${encodeURIComponent(from.toISOString())}`} className="block h-full hover:opacity-80">
      <StatCard
        value={isError ? '—' : won}
        label="Won this month"
        sublabel={isError
          ? reportErrorLabel(error)
          : revenue > 0 ? `${formatCurrency(revenue, report?.currency)} revenue` : (won > 0 ? 'No values entered' : 'No customers won yet')}
        color="text-green-600"
        loading={isLoading}
      />
    </Link>
  );
};

// ---- Reports-driven widgets --------------------------------------------

export const TopRepWidget = () => {
  const { data, isLoading } = useQuery(
    ['widget', 'reports', 'top-rep'],
    () => {
      const to = new Date();
      const from = new Date(to.getTime() - 90 * 86400000);
      return api.get('/reports/users', {
        params: { from: from.toISOString(), to: to.toISOString() },
        silent: true,
      }).then((r) => r.data);
    },
    { staleTime: 5 * 60 * 1000 }
  );
  const top = (data?.users || []).filter((u) => u.userId).slice(0, 1)[0];
  return (
    <Link to="/reports/users" className="block h-full hover:opacity-80">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Top performer (90d)</div>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : !top ? (
        <p className="text-sm text-gray-400">No data yet.</p>
      ) : (
        <div>
          <div className="text-xl font-bold text-gray-900">{top.name}</div>
          <div className="text-sm text-gray-700 mt-1">
            {formatCurrency(top.revenue)} · {top.won} won · {top.winRate}% win rate
          </div>
        </div>
      )}
    </Link>
  );
};

export const ConversionFunnelWidget = () => {
  const { data, isLoading } = useQuery(
    ['widget', 'reports', 'sales'],
    () => api.get("/reports/sales", { silent: true }).then((r) => r.data),
    { staleTime: 5 * 60 * 1000 }
  );
  const funnel = data?.funnel || [];
  const max = Math.max(...funnel.map((f) => f.count), 1);
  return (
    <div className="h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Conversion funnel · current</div>
        <Link to="/reports" className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1">
          Open reports <ArrowUpRight size={12} />
        </Link>
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : funnel.length === 0 ? (
        <p className="text-sm text-gray-400">No stages yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {funnel.map((s) => (
            <li key={s.stageId} className="flex items-center gap-3 text-xs">
              <span className="w-28 truncate text-gray-700">{s.name}</span>
              <div className="flex-1 h-5 bg-gray-100 rounded-md overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{ width: `${(s.count / max) * 100}%`, backgroundColor: s.color || '#6366F1' }}
                />
              </div>
              <span className="w-10 text-right font-mono font-medium text-gray-900">{s.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---- Stats overview block — the four legacy cards bundled as a single
// full-width widget. Design intentionally identical to the original
// `Stats Cards` JSX in Dashboard.js (colored icon panels, motion hover,
// `card` classes) so the operator can't tell anything moved beyond now
// being inside the customizable grid.

const formatTotalValue = (overview) => {
  if (!overview) return formatCurrency(0);
  if (overview.totalValue !== undefined && overview.totalValue !== null && overview.currency) {
    return formatCurrency(overview.totalValue, overview.currency);
  }
  return formatCurrency(0);
};

export const StatsOverviewWidget = () => {
  const navigate = useNavigate();
  const { data: stats } = useQuery(
    ['widget', 'stats', 'all'],
    () => api.get("/leads/stats/overview", { params: { timeRange: "all" }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );

  const cards = [
    {
      title: 'Total Leads',
      value: stats?.overview?.totalLeads || 0,
      icon: Users,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100',
      onClick: () => navigate('/leads'),
    },
    {
      title: 'New Leads',
      value: stats?.overview?.newLeads || 0,
      icon: TrendingUp,
      color: 'text-success-600',
      bgColor: 'bg-success-100',
      onClick: () => navigate('/leads?status=new'),
    },
    {
      title: 'High Priority',
      value: stats?.overview?.highPriorityLeads || 0,
      icon: AlertTriangle,
      color: 'text-warning-600',
      bgColor: 'bg-warning-100',
      onClick: () => navigate('/leads?priority=high'),
    },
    {
      title: 'Total Value',
      value: formatTotalValue(stats?.overview),
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      // Sums the open leads above (won and Do Not Call excluded).
      onClick: () => navigate('/leads'),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 -m-4">
      {cards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={stat.onClick}
            className="card cursor-pointer hover:shadow-lg transition-all duration-200"
          >
            <div className="card-body">
              <div className="flex items-center">
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${stat.bgColor}`}>
                  <Icon size={24} className={stat.color} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// ---- Lifetime / overview cards (the originals) -------------------------

export const TotalLeadsWidget = () => {
  const { data: stats, isLoading } = useQuery(
    ['widget', 'stats', 'all'],
    () => api.get("/leads/stats/overview", { params: { timeRange: "all" }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  return (
    <Link to="/leads" className="block h-full hover:opacity-80">
      <StatCard
        value={stats?.overview?.totalLeads ?? 0}
        label="Total leads"
        sublabel="All-time, in this organization"
        loading={isLoading}
      />
    </Link>
  );
};

export const NewLeadsTotalWidget = () => {
  const { data: stats, isLoading } = useQuery(
    ['widget', 'stats', 'all'],
    () => api.get("/leads/stats/overview", { params: { timeRange: "all" }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  return (
    <Link to="/leads" className="block h-full hover:opacity-80">
      <StatCard
        value={stats?.overview?.newLeads ?? 0}
        label="New leads"
        sublabel="Stage = New, awaiting first contact"
        color="text-green-600"
        loading={isLoading}
      />
    </Link>
  );
};

export const HighPriorityWidget = () => {
  const { data: stats, isLoading } = useQuery(
    ['widget', 'stats', 'all'],
    () => api.get("/leads/stats/overview", { params: { timeRange: "all" }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const v = stats?.overview?.highPriorityLeads ?? 0;
  return (
    <Link to="/leads?priority=high" className="block h-full hover:opacity-80">
      <StatCard
        value={v}
        label="High priority"
        sublabel={v > 0 ? 'Open leads marked High' : 'Nothing flagged'}
        color={v > 0 ? 'text-amber-600' : 'text-gray-900'}
        loading={isLoading}
      />
    </Link>
  );
};

export const TotalValueWidget = () => {
  // Revenue from every customer ever won (the sales report counts wins by
  // won date; /leads/stats/overview leaves won leads out).
  const { data: report, isLoading, isError, error } = useQuery(
    ['widget', 'reports', 'won-all-time'],
    () => api.get('/reports/sales', { params: { from: new Date(0).toISOString(), to: new Date().toISOString() }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const v = report?.winRate?.totalRevenue ?? 0;
  return (
    <Link to="/customers" className="block h-full hover:opacity-80">
      <StatCard
        value={isError ? '—' : formatCurrency(v, report?.currency)}
        label="Total value"
        sublabel={isError ? reportErrorLabel(error) : 'Won leads · revenue'}
        color="text-green-600"
        loading={isLoading}
      />
    </Link>
  );
};

// ---- "My Tasks" full-width list (re-uses the existing dashboard task panel)

export const MyTasksFullWidget = () => {
  // The existing DashboardTasksWidget component already paints overdue/today/
  // this-week sections with its own styling — wrap it instead of reimplementing.
  return <DashboardTasksWidget />;
};

// ---- Quick actions (formerly the static three-button strip) ---------------

const QUICK_ACTIONS = [
  { title: 'Add New Lead',           description: 'Manually add a new lead',         icon: Plus,   href: '/leads/new', color: 'bg-primary-500',  hoverColor: 'hover:bg-primary-600' },
  { title: 'Upload Business Card',   description: 'Analyze business card with AI',   icon: Upload, href: '/upload',    color: 'bg-success-500',  hoverColor: 'hover:bg-success-600' },
  { title: 'View Leads',             description: 'List all leads',                  icon: Eye,    href: '/leads',     color: 'bg-warning-500',  hoverColor: 'hover:bg-warning-600' },
];

export const QuickActionsWidget = () => (
  <div className="h-full">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Quick actions</div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {QUICK_ACTIONS.map((action, index) => {
        const Icon = action.icon;
        return (
          <motion.div
            key={action.title}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.06 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link
              to={action.href}
              className={`block p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-all duration-200 ${action.hoverColor}`}
            >
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${action.color} text-white mb-3`}>
                <Icon size={18} />
              </div>
              <h4 className="text-sm font-medium text-gray-900 mb-1">{action.title}</h4>
              <p className="text-xs text-gray-600">{action.description}</p>
            </Link>
          </motion.div>
        );
      })}
    </div>
  </div>
);

// ---- Status / source distribution charts (formerly static side-by-side) ---

export const StatusDistributionWidget = () => {
  const { data: stats } = useQuery(
    ['widget', 'stats', 'status-dist'],
    () => api.get('/leads/stats/overview', { params: { timeRange: 'all' }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const breakdown = stats?.statusBreakdown || [];
  return (
    <div className="h-full">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Status distribution</div>
      {breakdown.length === 0 ? (
        <p className="text-sm text-gray-400">No data available yet.</p>
      ) : (
        <div className="space-y-2">
          {breakdown.map((s) => (
            <div key={s._id} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-2.5 h-2.5 rounded-full mr-2 status-${s._id}`} />
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {(s._id || 'unknown').replace('_', ' ')}
                </span>
              </div>
              <span className="text-sm text-gray-600 tabular-nums">{s.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SourceDistributionWidget = () => {
  const { data: stats } = useQuery(
    ['widget', 'stats', 'source-dist'],
    () => api.get('/leads/stats/overview', { params: { timeRange: 'all' }, silent: true }).then((r) => r.data),
    { staleTime: 60 * 1000 }
  );
  const breakdown = stats?.sourceBreakdown || [];
  return (
    <div className="h-full">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Source distribution</div>
      {breakdown.length === 0 ? (
        <p className="text-sm text-gray-400">No data available yet.</p>
      ) : (
        <div className="space-y-2">
          {breakdown.map((s) => (
            <div key={s._id} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-2.5 h-2.5 rounded-full mr-2 source-${s._id}`} />
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {(s._id || 'unknown').replace('_', ' ')}
                </span>
              </div>
              <span className="text-sm text-gray-600 tabular-nums">{s.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Recent activities (full panel — formerly the static block at the
//       bottom of Dashboard.js. Distinct from the smaller RecentActivityWidget
//       which is a compact list — this one shows colored type icons, more
//       items, and links per row).
export const RecentActivitiesFullWidget = () => {
  const { data: activities = [] } = useQuery(
    ['widget', 'activities-full'],
    () => api.get('/activities', { params: { limit: 10 }, silent: true }).then((r) => r.data?.activities || r.data || []),
    { staleTime: 30 * 1000 }
  );
  return (
    <div className="h-full">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Recent activities</div>
      {activities.length === 0 ? (
        <div className="text-center py-6">
          <Activity size={32} className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">Activities will appear here when you start interacting with your leads.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {activities.map((activity) => {
            const tone =
              activity.type === 'lead_created' ? 'bg-green-100 text-green-600' :
              activity.type === 'lead_updated' ? 'bg-blue-100 text-blue-600' :
              activity.type === 'lead_deleted' ? 'bg-red-100 text-red-600' :
              activity.type === 'csv_import' ? 'bg-cyan-100 text-cyan-600' :
              activity.type === 'note_added' ? 'bg-purple-100 text-purple-600' :
              activity.type === 'note_deleted' ? 'bg-orange-100 text-orange-600' :
              activity.type === 'bulk_email_created' ? 'bg-indigo-100 text-indigo-600' :
              activity.type === 'bulk_email_started' ? 'bg-emerald-100 text-emerald-600' :
              activity.type === 'bulk_email_completed' ? 'bg-teal-100 text-teal-600' :
              activity.type === 'email_sent' ? 'bg-cyan-100 text-cyan-600' :
              'bg-gray-100 text-gray-600';
            const Icon =
              activity.type === 'lead_created' ? Plus :
              activity.type === 'lead_deleted' ? AlertTriangle :
              activity.type === 'csv_import' ? Upload :
              activity.type === 'note_added' ? FileText :
              activity.type === 'note_deleted' ? Trash2 :
              activity.type === 'bulk_email_created' ? Send :
              activity.type === 'bulk_email_started' ? Play :
              activity.type === 'bulk_email_completed' ? CheckCircle :
              activity.type === 'email_sent' ? Mail :
              Activity;
            return (
              <div key={activity._id} className="flex items-start gap-3 p-2 rounded hover:bg-gray-50">
                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${tone}`}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-900 truncate">{activity.description || activity.message}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {new Date(activity.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- Activity widget ---------------------------------------------------

export const RecentActivityWidget = () => {
  const { data: activities = [], isLoading } = useQuery(
    ['widget', 'activities'],
    () => api.get("/activities", { params: { limit: 8 }, silent: true }).then((r) => r.data?.activities || r.data || []),
    { staleTime: 30 * 1000 }
  );
  return (
    <div className="h-full">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Recent activity</div>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : activities.length === 0 ? (
        <p className="text-sm text-gray-400">No activity yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {activities.slice(0, 6).map((a) => (
            <li key={a._id} className="text-xs text-gray-700 flex items-start gap-2">
              <CheckSquare size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="truncate block">{a.message || a.description || a.title || 'Activity'}</span>
                {a.createdAt && (
                  <span className="text-[10px] text-gray-400">{new Date(a.createdAt).toLocaleString()}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
